import { oc } from "@orpc/contract";
import { z } from "zod";

// --- Response schemas ---

/**
 * An organization namespace (`core_namespaces`): groups the tasks (which it
 * also numbers, e.g. "TASK-42"), task types, workflows, agents and workbooks
 * that make up one module.
 */
export const NamespaceResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  key: z.string(),
  lastTaskNumber: z.number().int(),
  isDefault: z.boolean(),
  description: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const NamespaceMemberSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  isActive: z.boolean(),
});

/** Everything a namespace currently holds — one read for a module's footprint. */
export const NamespaceContentsResponseSchema = z.object({
  namespace: NamespaceResponseSchema,
  taskCount: z.number().int(),
  taskTypes: z.array(NamespaceMemberSummarySchema.omit({ isActive: true })),
  workflows: z.array(NamespaceMemberSummarySchema),
  agents: z.array(NamespaceMemberSummarySchema),
  /** Only the caller's own: Workbooks are private to their Owner. */
  workbooks: z.array(NamespaceMemberSummarySchema.omit({ isActive: true })),
});

// Short uppercase prefix (e.g. "TASK", "ENG"). Alphanumeric, 1-10 chars.
// Exported as the single source of truth for namespace-key validation
// (the task type admin UI validates inline "create namespace" input with it).
export const NamespaceKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(10)
  .regex(/^[A-Za-z0-9]+$/, "Key must be alphanumeric");

// --- Input schemas ---

const NamespaceIdInputSchema = z.object({ id: z.uuid() });

export const CreateNamespaceInputSchema = z.object({
  name: z.string().min(1).max(255),
  key: NamespaceKeySchema,
  description: z.string().max(10000).nullable().optional(),
});

export const UpdateNamespaceInputSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(255).optional(),
  key: NamespaceKeySchema.optional(),
  description: z.string().max(10000).nullable().optional(),
});

// --- Contracts ---

export const namespacesContracts = {
  list: oc.input(z.object({})).output(z.array(NamespaceResponseSchema)).route({
    summary: "List namespaces",
    description:
      "Lists the organization's namespaces, the module groupings that hold and number tasks, task types, workflows, agents and workbooks, with key, default flag and description.",
    method: "GET",
  }),

  getContents: oc
    .input(NamespaceIdInputSchema)
    .output(NamespaceContentsResponseSchema)
    .route({
      summary: "Get a namespace's contents",
      description:
        "Returns one namespace with its task count and the task types, workflows, agents and the caller's own workbooks it contains.",
      method: "GET",
    }),

  create: oc
    .input(CreateNamespaceInputSchema)
    .output(NamespaceResponseSchema)
    .route({
      summary: "Create a namespace",
      description:
        "Creates a namespace with a name, short alphanumeric key used to number its tasks (e.g. TASK-42), and optional description.",
      method: "POST",
    }),

  update: oc
    .input(UpdateNamespaceInputSchema)
    .output(NamespaceResponseSchema)
    .route({
      summary: "Update a namespace",
      description:
        "Renames a namespace or changes its key or description. Fails with a conflict if another namespace already uses the key.",
      method: "PUT",
    }),

  archive: oc
    .input(NamespaceIdInputSchema)
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Archive a namespace",
      description:
        "Archives a non-default namespace once it has no tasks, workflows or agents; its workbooks are ungrouped. Fails while members remain.",
      method: "DELETE",
    }),

  setDefault: oc
    .input(NamespaceIdInputSchema)
    .output(NamespaceResponseSchema)
    .route({
      summary: "Set the default namespace",
      description:
        "Makes a namespace the organization's default, clearing the previous default. Returns the updated namespace.",
      method: "PUT",
    }),
} as const;

// --- Type exports ---

export type NamespaceResponse = z.infer<typeof NamespaceResponseSchema>;
export type NamespaceContentsResponse = z.infer<
  typeof NamespaceContentsResponseSchema
>;
export type CreateNamespaceInput = z.infer<typeof CreateNamespaceInputSchema>;
export type UpdateNamespaceInput = z.infer<typeof UpdateNamespaceInputSchema>;
